import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Table,
  Tag,
  Button,
  Modal as AntdModal,
  Input,
  Select,
  Space,
  Tooltip,
  Popconfirm,
  Checkbox,
  DatePicker,
  Alert,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  UnorderedListOutlined,
  MessageOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/data/redux/slices/ToastSlice";
import api from "../../../../core/axios/axiosInstance";
import { useDebounce } from "../../../../core/hooks/useDebounce";

// using single DatePicker for created date filter

const ManageTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filterStatus, setFilterStatus] = useState([
    "pending",
    "replied",
    "closed",
  ]);
  const [filterTypes, setFilterTypes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 500);
  const [replyModalVisible, setReplyModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const dispatch = useDispatch();

  const inquiryTypes = [
    "General",
    "Billing & Subscription",
    "Support",
    "Bug Report",
    "Others",
  ];

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line
  }, [
    pagination.current,
    pagination.pageSize,
    filterStatus,
    filterTypes,
    selectedDate,
    debouncedSearch,
  ]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      let params = {
        page: pagination.current,
        limit: pagination.pageSize,
      };

      // Add status filter
      if (filterStatus.length > 0 && filterStatus.length < 3) {
        params.status = filterStatus.join(",");
      }

      // Add type filter
      if (filterTypes.length > 0) {
        params.inquiryType = filterTypes.join(",");
      }

      // Add created date filter (single date) - send as startDate/endDate equal to selected date
      if (selectedDate) {
        const d = selectedDate.format("YYYY-MM-DD");
        params.startDate = d;
        params.endDate = d;
      }

      // Add search
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const response = await api.get("/admin/help-support", { params });
      if (response.data.status === "success") {
        setTickets(response.data.data.tickets);
        setPagination({
          current: response.data.data.pagination.currentPage,
          pageSize: response.data.data.pagination.limit,
          total: response.data.data.pagination.totalTickets,
        });
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message: "Failed to fetch tickets",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination({ ...pagination, total: pagination.total });
  };

  const handleStatusFilterChange = (status) => {
    setFilterStatus((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTypeFilterChange = (type) => {
    setFilterTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setFilterStatus(["pending", "replied", "closed"]);
    setFilterTypes([]);
    setSelectedDate(null);
    setSearchText("");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const openReplyModal = async (ticket) => {
    setSelectedTicket(ticket);
    setReplyText("");
    setReplyModalVisible(true);

    // Fetch latest ticket data with messages
    try {
      const response = await api.get(`/admin/help-support/${ticket._id}`);
      if (response.data.status === "success") {
        setSelectedTicket(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
    }
  };

  const closeReplyModal = () => {
    setReplyModalVisible(false);
    setSelectedTicket(null);
    setReplyText("");
  };

  const handleReplySubmit = async () => {
    if (!selectedTicket || !replyText.trim()) return;
    setReplyLoading(true);
    try {
      const response = await api.post(
        `/admin/help-support/${selectedTicket._id}/reply`,
        {
          message: replyText,
        }
      );
      if (response.data.status === "success") {
        dispatch(
          showToast({
            heading: "Success",
            message: "Message sent successfully",
            variant: "success",
          })
        );
        setReplyText("");
        // Refresh ticket data to show new message
        const updatedResponse = await api.get(
          `/admin/help-support/${selectedTicket._id}`
        );
        if (updatedResponse.data.status === "success") {
          setSelectedTicket(updatedResponse.data.data);
        }
        fetchTickets();
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message: error?.response?.data?.message || "Failed to send reply",
          variant: "danger",
        })
      );
    } finally {
      setReplyLoading(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      setDeleteLoadingId(ticketId);
      const response = await api.post(`/admin/help-support/${ticketId}/close`);
      if (response.data.status === "success") {
        dispatch(
          showToast({
            heading: "Success",
            message: "Ticket closed successfully",
            variant: "success",
          })
        );
        fetchTickets();
        // If modal open for this ticket, refresh it
        if (selectedTicket && selectedTicket._id === ticketId) {
          const resp = await api.get(`/admin/help-support/${ticketId}`);
          if (resp.data.status === "success") setSelectedTicket(resp.data.data);
        }
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message: error?.response?.data?.message || "Failed to close ticket",
          variant: "danger",
        })
      );
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    setBulkDeleting(true);
    try {
      await api.delete("/admin/help-support/bulk", {
        data: { ticketIds: selectedRowKeys },
      });
      dispatch(showToast({ message: `${selectedRowKeys.length} tickets deleted`, variant: "success" }));
      setSelectedRowKeys([]);
      fetchTickets();
    } catch (err) {
      dispatch(showToast({ message: err?.response?.data?.message || "Bulk delete failed", variant: "danger" }));
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    setDeleteLoadingId(ticketId);
    try {
      const response = await api.delete(`/admin/help-support/${ticketId}`);
      if (response.data.status === "success") {
        dispatch(
          showToast({
            heading: "Success",
            message: "Ticket deleted successfully",
            variant: "success",
          })
        );
        fetchTickets();
      }
    } catch (error) {
      dispatch(
        showToast({
          heading: "Error",
          message: error?.response?.data?.message || "Failed to delete ticket",
          variant: "danger",
        })
      );
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns = [
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const status = record.status || "pending";

        if (status === "closed") {
          return (
            <Tag color="red">
              <CloseCircleOutlined /> Closed
            </Tag>
          );
        } else if (status === "replied") {
          return (
            <Tag color="green">
              <CheckCircleOutlined /> Replied
            </Tag>
          );
        } else {
          return (
            <Tag color="orange">
              <CloseCircleOutlined /> Pending
            </Tag>
          );
        }
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          {text ||
            (record.userId &&
              `${record.userId.firstname} ${record.userId.lastname}`)}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "emailaddresses",
      key: "email",
      render: (emails, record) => (
        <span>
          {emails && emails.length > 0
            ? emails[0]
            : record.userId?.email || "N/A"}
        </span>
      ),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Type",
      dataIndex: "inquiryType",
      key: "inquiryType",
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View/Reply">
            <Button
              type="primary"
              icon={<MessageOutlined />}
              size="small"
              onClick={() => openReplyModal(record)}
            >
              Chat
            </Button>
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this ticket?"
            onConfirm={() => handleDeleteTicket(record._id)}
            okText="Yes"
            cancelText="No"
            disabled={deleteLoadingId === record._id}
          >
            <Button
              type="primary"
              danger={true}
              icon={<DeleteOutlined />}
              size="small"
              loading={deleteLoadingId === record._id}
            >
              Delete
            </Button>
          </Popconfirm>
          {record.status !== "closed" && (
            <Popconfirm
              title="Are you sure you want to close this ticket?"
              onConfirm={() => handleCloseTicket(record._id)}
              okText="Yes"
              cancelText="No"
              disabled={deleteLoadingId === record._id}
            >
              <Button
                type="default"
                danger
                size="small"
                loading={deleteLoadingId === record._id}
              >
                Close
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3 className="page-title">Manage Tickets</h3>
                  {/* <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.dashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">Tickets</li>
                  </ul> */}
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body ">
                <div className="d-flex flex-md-row flex-column justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold mb-0 align-self-md-center align-self-start mb-md-0 mb-3 ms-1">
                    All Tickets
                  </h5>
                  <div className="d-flex">
                    {/* Filter Dropdown */}

                    <div className="form-sorts dropdown me-2">
                      <Link
                        to=""
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-filter-share" />
                        Filter
                      </Link>
                      <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                        <div className="filter-set-view">
                          <div className="filter-set-head">
                            <h4>
                              <i className="ti ti-filter-share" />
                              Filter
                            </h4>
                          </div>
                          <div className="accordion" id="accordionExample">
                            {/* Status Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to=""
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#Status"
                                  aria-expanded="false"
                                  aria-controls="Status"
                                >
                                  Status
                                </Link>
                              </div>
                              <div
                                className="filter-set-contents accordion-collapse collapse"
                                id="Status"
                                data-bs-parent="#accordionExample"
                              >
                                <div className="filter-content-list">
                                  <ul>
                                    <li>
                                      <div className="filter-checks">
                                        <label className="checkboxs">
                                          <input
                                            type="checkbox"
                                            checked={filterStatus.includes(
                                              "pending"
                                            )}
                                            onChange={() =>
                                              handleStatusFilterChange(
                                                "pending"
                                              )
                                            }
                                          />
                                          <span className="checkmarks" />
                                          <span />
                                          Pending
                                        </label>
                                      </div>
                                    </li>
                                    <li>
                                      <div className="filter-checks">
                                        <label className="checkboxs">
                                          <input
                                            type="checkbox"
                                            checked={filterStatus.includes(
                                              "replied"
                                            )}
                                            onChange={() =>
                                              handleStatusFilterChange(
                                                "replied"
                                              )
                                            }
                                          />
                                          <span className="checkmarks" />
                                          <span />
                                          Replied
                                        </label>
                                      </div>
                                    </li>
                                    <li>
                                      <div className="filter-checks">
                                        <label className="checkboxs">
                                          <input
                                            type="checkbox"
                                            checked={filterStatus.includes(
                                              "closed"
                                            )}
                                            onChange={() =>
                                              handleStatusFilterChange("closed")
                                            }
                                          />
                                          <span className="checkmarks" />
                                          <span />
                                          Closed
                                        </label>
                                      </div>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Type Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to=""
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#Type"
                                  aria-expanded="false"
                                  aria-controls="Type"
                                >
                                  Inquiry Type
                                </Link>
                              </div>
                              <div
                                className="filter-set-contents accordion-collapse collapse"
                                id="Type"
                                data-bs-parent="#accordionExample"
                              >
                                <div className="filter-content-list">
                                  <ul>
                                    {inquiryTypes.map((type, index) => (
                                      <li key={index}>
                                        <div className="filter-checks">
                                          <label className="checkboxs">
                                            <input
                                              type="checkbox"
                                              checked={filterTypes.includes(
                                                type
                                              )}
                                              onChange={() =>
                                                handleTypeFilterChange(type)
                                              }
                                            />
                                            <span className="checkmarks" />
                                            <span />
                                            {type}
                                          </label>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Date Range Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to=""
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#DateRange"
                                  aria-expanded="false"
                                  aria-controls="DateRange"
                                >
                                  Created Date
                                </Link>
                              </div>
                              <div
                                className="filter-set-contents accordion-collapse collapse"
                                id="DateRange"
                                data-bs-parent="#accordionExample"
                              >
                                <div className="filter-content-list">
                                  <div className="p-2">
                                    <DatePicker
                                      value={selectedDate}
                                      onChange={handleDateChange}
                                      style={{ width: "100%" }}
                                      format="YYYY-MM-DD"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="filter-reset-btns">
                            <div className="row">
                              <div className="col-6"></div>
                              <div className="col-6">
                                <Link
                                  to=""
                                  className="btn btn-primary"
                                  onClick={resetFilters}
                                >
                                  Reset
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Search Bar */}
                    <div className="icon-form mb-3 me-2 mb-sm-0">
                      <span className="form-icon">
                        <i className="ti ti-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        style={{
                          width: "300px",
                        }}
                        placeholder="Search by name, email or subject..."
                        value={searchText}
                        onChange={handleSearchChange}
                      />
                    </div>
                    {selectedRowKeys.length > 0 && (
                      <Popconfirm
                        title={`Delete ${selectedRowKeys.length} selected ticket(s)?`}
                        onConfirm={handleBulkDelete}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          loading={bulkDeleting}
                        >
                          Delete Selected ({selectedRowKeys.length})
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                </div>
                <Alert
                  description="Tickets not replied to within 48 hours are closed automatically."
                  type="info"
                  showIcon
                  style={{
                    padding: ".6rem",
                    marginBottom: "15px",
                    // maxWidth:"500px"
                  }}
                />
                <div className="table-responsive custom-table">
                  <Table
                    columns={columns}
                    dataSource={tickets}
                    rowKey="_id"
                    loading={loading}
                    rowSelection={rowSelection}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    bordered
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AntdModal
        title={
          selectedTicket
            ? `Support Chat: ${selectedTicket.subject}`
            : "Support Chat"
        }
        open={replyModalVisible}
        onCancel={closeReplyModal}
        footer={null}
        width={700}
        destroyOnClose
      >
        {selectedTicket && (
          <div>
            <div className="mb-3">
              <div className="row d-flex gap-2">
                <div className="">
                  <strong>From:</strong>{" "}
                  {selectedTicket.name ||
                    (selectedTicket.userId &&
                      `${selectedTicket.userId.firstname} ${selectedTicket.userId.lastname}`)}
                </div>
                <div className="">
                  <strong>Email:</strong>{" "}
                  {selectedTicket.emailaddresses &&
                  selectedTicket.emailaddresses.length > 0
                    ? selectedTicket.emailaddresses[0]
                    : selectedTicket.userId?.email || "N/A"}
                </div>
                <div className="">
                  <strong>Phone Number:</strong>{" "}
                  {selectedTicket.phonenumbers &&
                  selectedTicket.phonenumbers.length > 0
                    ? `+${selectedTicket.phonenumbers[0].countryCode} ${selectedTicket.phonenumbers[0].number}`
                    : "N/A"}
                </div>
                <div className="">
                  <strong>Type:</strong>{" "}
                  <Tag color="blue">{selectedTicket.inquiryType}</Tag>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div
              style={{
                height: "400px",
                overflowY: "auto",
                border: "1px solid #f0f0f0",
                padding: "15px",
                marginBottom: "15px",
                backgroundColor: "#fafafa",
              }}
            >
              {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                selectedTicket.messages.map((message, index) => (
                  <div
                    key={message._id || index}
                    style={{
                      display: "flex",
                      justifyContent:
                        message.sender === "customer"
                          ? "flex-start"
                          : "flex-end",
                      marginBottom: "15px",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "70%",
                        padding: "10px 15px",
                        borderRadius: "15px",
                        backgroundColor:
                          message.sender === "customer" ? "#e3f2fd" : "#f3e5f5",
                        border: `1px solid ${
                          message.sender === "customer" ? "#bbdefb" : "#e1bee7"
                        }`,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "12px",
                          marginBottom: "5px",
                        }}
                      >
                        {message.sender === "customer"
                          ? selectedTicket.name ||
                            (selectedTicket.userId &&
                              `${selectedTicket.userId.firstname} ${selectedTicket.userId.lastname}`) ||
                            "Customer"
                          : "Support Team"}
                      </div>
                      <div>{message.content}</div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          marginTop: "5px",
                        }}
                      >
                        {new Date(message.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Fallback for old tickets without messages array
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "70%",
                      padding: "10px 15px",
                      borderRadius: "15px",
                      backgroundColor: "#e3f2fd",
                      border: "1px solid #bbdefb",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "12px",
                        marginBottom: "5px",
                      }}
                    >
                      {selectedTicket.name ||
                        (selectedTicket.userId &&
                          `${selectedTicket.userId.firstname} ${selectedTicket.userId.lastname}`) ||
                        "Customer"}
                    </div>
                    <div>{selectedTicket.message}</div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginTop: "5px",
                      }}
                    >
                      {new Date(selectedTicket.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {/* Legacy admin reply */}
              {selectedTicket.adminReply && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "70%",
                      padding: "10px 15px",
                      borderRadius: "15px",
                      backgroundColor: "#f3e5f5",
                      border: "1px solid #e1bee7",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "12px",
                        marginBottom: "5px",
                      }}
                    >
                      Support Team (Legacy)
                    </div>
                    <div>{selectedTicket.adminReply}</div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginTop: "5px",
                      }}
                    >
                      {new Date(selectedTicket.lastRepliedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            {selectedTicket.status === "closed" ? (
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#fff7e6",
                  border: "1px solid #ffd591",
                  borderRadius: "4px",
                  textAlign: "center",
                }}
              >
                <CloseCircleOutlined
                  style={{ color: "#fa8c16", marginRight: "8px" }}
                />
                This ticket is closed. No further replies are allowed.
              </div>
            ) : (
              <div style={{ display: "flex", gap: "10px" }}>
                <Input.TextArea
                  rows={3}
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onPressEnter={(e) => {
                    if (e.ctrlKey) {
                      handleReplySubmit();
                    }
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <Button
                    type="primary"
                    loading={replyLoading}
                    onClick={handleReplySubmit}
                    style={{ height: "fit-content" }}
                  >
                    Send
                  </Button>
                  <Button
                    onClick={closeReplyModal}
                    style={{ height: "fit-content" }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
            {selectedTicket.status !== "closed" && (
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
              >
                Press Ctrl+Enter to send
              </div>
            )}
          </div>
        )}
      </AntdModal>
    </div>
  );
};

export default ManageTickets;
